import { createContext, useContext, useEffect, useState, ReactNode } from 'react'
import { User, Session, AuthError } from '@supabase/supabase-js'
import { supabase } from '../lib/supabase'
import { getOrCreateProfile } from '../lib/profileService'
import { signUpUser, signInUser } from '../lib/authService'

// Tipos para o perfil do usuário
interface UserProfile {
  user_id: string
  username: string | null
  email: string | null
  created_at: string
  updated_at: string
}

// Tipos para empresa
interface Company {
  id: string
  name: string
  owner_id: string
  created_at: string
}



// Interface do contexto de autenticação
interface AuthContextType {
  // Estado da autenticação
  user: User | null
  session: Session | null
  profile: UserProfile | null
  companies: Company[]
  currentCompany: Company | null
  userRole: 'owner' | 'admin' | 'collaborator' | null
  
  // Estados de carregamento
  loading: boolean
  profileLoading: boolean
  
  // Métodos de autenticação
  signUp: (email: string, password: string, username?: string) => Promise<{ user: User | null; error: AuthError | null }>
  signIn: (email: string, password: string) => Promise<{ user: User | null; error: AuthError | null }>
  signOut: () => Promise<{ error: AuthError | null }>
  
  // Métodos de perfil
  updateProfile: (updates: Partial<Pick<UserProfile, 'username' | 'email'>>) => Promise<{ error: Error | null }>
  
  // Métodos de empresa
  createCompany: (name: string) => Promise<{ company: Company | null; error: Error | null }>
  switchCompany: (companyId: string) => Promise<{ error: Error | null }>
  inviteUserToCompany: (email: string, role: 'admin' | 'collaborator') => Promise<{ error: Error | null }>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

// Hook para usar o contexto de autenticação
export function useAuth() {
  const context = useContext(AuthContext)
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider')
  }
  return context
}

// Provider do contexto de autenticação
interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [session, setSession] = useState<Session | null>(null)
  const [profile, setProfile] = useState<UserProfile | null>(null)
  const [companies, setCompanies] = useState<Company[]>([])
  const [currentCompany, setCurrentCompany] = useState<Company | null>(null)
  const [userRole, setUserRole] = useState<'owner' | 'admin' | 'collaborator' | null>(null)
  const [loading, setLoading] = useState(true)
  const [profileLoading, setProfileLoading] = useState(false)

  // Carregar perfil do usuário
  const loadUserProfile = async (userId: string, userEmail?: string, userMetadata?: any) => {
    try {
      setProfileLoading(true)
      
      console.log('🔄 Carregando perfil para usuário:', userId)
      
      // Usar o novo serviço de perfil
      const { data: profileData, error: profileError } = await getOrCreateProfile(
        userId,
        userEmail,
        userMetadata?.username
      )

      if (profileError) {
        console.error('❌ Erro ao carregar/criar perfil:', profileError)
        return
      }

      if (profileData) {
        console.log('✅ Perfil carregado/criado:', profileData)
        setProfile(profileData)
      } else {
        console.error('❌ Nenhum perfil retornado')
        return
      }

      // Buscar empresas do usuário
      const { data: companiesData, error: companiesError } = await supabase
        .from('company_users')
        .select(`
          company_id,
          role,
          companies (
            id,
            name,
            owner_id,
            created_at
          )
        `)
        .eq('user_id', userId)

      if (companiesError) {
        console.error('Erro ao carregar empresas:', companiesError)
        return
      }

      // Processar dados das empresas
      const userCompanies: Company[] = []
      let currentUserRole: 'owner' | 'admin' | 'collaborator' | null = null
      
      if (companiesData) {
        for (const item of companiesData) {
          if (item.companies) {
            const company = item.companies as unknown as Company
            userCompanies.push(company)
            
            // Se é a primeira empresa, definir como atual
            if (userCompanies.length === 1) {
              setCurrentCompany(company)
              currentUserRole = item.role as 'owner' | 'admin' | 'collaborator'
            }
          }
        }
      }
      
      setCompanies(userCompanies)

      // Definir empresa atual (empresa própria tem prioridade)
      if (userCompanies.length > 0) {
        const ownedCompany = userCompanies.find(company => company.owner_id === userId)
        if (ownedCompany) {
          setCurrentCompany(ownedCompany)
          // Buscar role na empresa própria
          const ownedCompanyData = companiesData?.find(item => 
            item.companies && (item.companies as any).id === ownedCompany.id
          )
          currentUserRole = ownedCompanyData?.role as 'owner' | 'admin' | 'collaborator' || 'owner'
        }
      }
      
      setUserRole(currentUserRole)

    } catch (error) {
      console.error('Erro ao carregar dados do usuário:', error)
    } finally {
      setProfileLoading(false)
    }
  }

  // Inicializar autenticação
  useEffect(() => {
    // Obter sessão atual
    supabase.auth.getSession().then(async ({ data: { session } }) => {
      setSession(session)
      setUser(session?.user ?? null)
      
      if (session?.user) {
        await loadUserProfile(session.user.id, session.user.email, session.user.user_metadata)
      }
      
      setLoading(false)
    })

    // Escutar mudanças na autenticação
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session)
        setUser(session?.user ?? null)
        
        if (session?.user) {
          await loadUserProfile(session.user.id, session.user.email, session.user.user_metadata)
        } else {
          setProfile(null)
          setCompanies([])
          setCurrentCompany(null)
          setUserRole(null)
        }
        
        setLoading(false)
      }
    )

    return () => subscription.unsubscribe()
  }, [])

  // Método de cadastro
  const signUp = async (email: string, password: string, username?: string) => {
    console.log('🔄 AuthContext: Iniciando cadastro')
    
    const result = await signUpUser({
      email,
      password,
      username
    })

    if (result.user && !result.error) {
      console.log('✅ AuthContext: Usuário criado, carregando perfil')
      // Carregar perfil após criação bem-sucedida
      await loadUserProfile(result.user.id, result.user.email, result.user.user_metadata)
    }

    return result
  }

  // Método de login
  const signIn = async (email: string, password: string) => {
    console.log('🔄 AuthContext: Iniciando login')
    
    const result = await signInUser(email, password)
    
    if (result.user && !result.error) {
      console.log('✅ AuthContext: Login realizado, carregando perfil')
      // Carregar perfil após login bem-sucedido
      await loadUserProfile(result.user.id, result.user.email, result.user.user_metadata)
    }

    return result
  }

  // Método de logout
  const signOut = async () => {
    const { error } = await supabase.auth.signOut()
    
    if (!error) {
      setProfile(null)
      setCompanies([])
      setCurrentCompany(null)
      setUserRole(null)
    }
    
    return { error }
  }

  // Atualizar perfil
  const updateProfile = async (updates: Partial<Pick<UserProfile, 'username' | 'email'>>) => {
    if (!user) {
      return { error: new Error('Usuário não autenticado') }
    }

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          ...updates,
          updated_at: new Date().toISOString()
        })
        .eq('user_id', user.id)

      if (error) {
        return { error: new Error(error.message) }
      }

      // Atualizar estado local
      if (profile) {
        setProfile({
          ...profile,
          ...updates,
          updated_at: new Date().toISOString()
        })
      }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  // Criar empresa
  const createCompany = async (name: string) => {
    // Verificar se o usuário está autenticado
    const { data: { user: currentUser } } = await supabase.auth.getUser()
    
    if (!currentUser) {
      console.error('Usuário não autenticado ao tentar criar empresa')
      return { company: null, error: new Error('Usuário não autenticado') }
    }

    console.log('Criando empresa para usuário:', currentUser.id)

    try {
      // Criar empresa
      const { data: companyData, error: companyError } = await supabase
        .from('companies')
        .insert({
          name,
          owner_id: currentUser.id
        })
        .select()
        .single()

      if (companyError) {
        return { company: null, error: new Error(companyError.message) }
      }

      // Adicionar usuário à empresa como owner
      const { error: userCompanyError } = await supabase
        .from('company_users')
        .insert({
          company_id: companyData.id,
          user_id: currentUser.id,
          role: 'owner'
        })

      if (userCompanyError) {
        return { company: null, error: new Error(userCompanyError.message) }
      }

      // Atualizar estado local
      setCompanies(prev => [...prev, companyData])
      setCurrentCompany(companyData)
      setUserRole('owner')

      return { company: companyData, error: null }
    } catch (error) {
      return { company: null, error: error as Error }
    }
  }

  // Trocar empresa atual
  const switchCompany = async (companyId: string) => {
    if (!user) {
      return { error: new Error('Usuário não autenticado') }
    }

    try {
      const company = companies.find(c => c.id === companyId)
      if (!company) {
        return { error: new Error('Empresa não encontrada') }
      }

      // Buscar role do usuário na empresa
      const { data: userCompanyData, error } = await supabase
        .from('company_users')
        .select('role')
        .eq('company_id', companyId)
        .eq('user_id', user.id)
        .single()

      if (error) {
        return { error: new Error(error.message) }
      }

      setCurrentCompany(company)
      setUserRole(userCompanyData.role)

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  // Convidar usuário para empresa
  const inviteUserToCompany = async (email: string, role: 'admin' | 'collaborator') => {
    if (!user || !currentCompany) {
      return { error: new Error('Usuário não autenticado ou empresa não selecionada') }
    }

    if (userRole !== 'owner' && userRole !== 'admin') {
      return { error: new Error('Permissão insuficiente para convidar usuários') }
    }

    try {
      // Buscar usuário pelo email
      const { data: profileData, error: profileError } = await supabase
        .from('profiles')
        .select('user_id')
        .eq('email', email)
        .single()

      if (profileError) {
        return { error: new Error('Usuário não encontrado') }
      }

      // Adicionar usuário à empresa
      const { error: inviteError } = await supabase
        .from('company_users')
        .insert({
          company_id: currentCompany.id,
          user_id: profileData.user_id,
          role
        })

      if (inviteError) {
        return { error: new Error(inviteError.message) }
      }

      return { error: null }
    } catch (error) {
      return { error: error as Error }
    }
  }

  const value: AuthContextType = {
    user,
    session,
    profile,
    companies,
    currentCompany,
    userRole,
    loading,
    profileLoading,
    signUp,
    signIn,
    signOut,
    updateProfile,
    createCompany,
    switchCompany,
    inviteUserToCompany
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  )
}
